import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { 
  TextGameStore,
  GameSession,
  GameFile,
  SavedGame,
  GameMessage,
  GameState,
  TextGameInput,
  TextGameResponse,
  ActionResult,
  GameMode
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'
import { getCurrentGeneratorModel } from '../lib/generatorModelSelection'

// 最大保存数の制限（メモリ管理）
const MAX_SESSIONS = 30
const MAX_SAVED_GAMES = 50
const MAX_GAME_FILES = 20
const MAX_MESSAGES_PER_SESSION = 200
const MAX_HISTORY_STATES = 50

// ID生成関数（タイムスタンプ＋ランダム文字列で重複防止）
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

function generateGameFileId(): string {
  return `game_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

function generateSaveId(): string {
  return `save_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

// 拡張ストア型（設計書の型に追加機能を含む）
interface ExtendedTextGameStore extends TextGameStore {
  // hydration状態
  isHydrated: boolean
  
  // メッセージ管理
  deleteMessagesAfter: (sessionId: string, messageId: string) => void
  retryUserMessage: (sessionId: string, messageId: string, apiKey: string, options?: { temperature?: number; responseLength?: string }) => Promise<ActionResult>
  
  // メモリ管理用のユーティリティ
  cleanupOldSessions: () => void
  cleanupOldSaves: () => void
  getMemoryUsage: () => {
    sessions: number
    saves: number
    gameFiles: number
    messages: number
  }
}

export const useTextGameStore = create<ExtendedTextGameStore>()(
  persist(
    (set, get) => ({
      // 基本状態
      sessions: [],
      activeSession: null,
      gameFiles: [],
      savedGames: [],
      
      // UI状態
      isLoading: false,
      error: null,
      currentMode: 'free' as GameMode,
      
      // hydration状態
      isHydrated: false,
      
      // セッション管理
      createSession: (title: string, mode: GameMode, gameFileId?: string) => {
        const sessionId = generateSessionId()
        const gameFile = gameFileId ? get().gameFiles.find(f => f.id === gameFileId) : undefined
        
        console.log('Creating session:', {
          title,
          mode,
          gameFileId,
          foundGameFile: !!gameFile,
          gameFileName: gameFile?.name,
          gameFileMode: gameFile?.mode,
          hasStructuredMode: !!gameFile?.structuredMode,
          allGameFiles: get().gameFiles.map(f => ({ id: f.id, name: f.name }))
        })
        
        const newSession: GameSession = {
          id: sessionId,
          gameFileId,
          mode,
          title,
          messages: [],
          gameState: {
            parameters: gameFile?.structuredMode?.parameters.reduce((acc, p) => ({
              ...acc,
              [p.name]: p.initialValue
            }), {}) || {},
            turn: 0,
            flags: {},
            phase: gameFile?.structuredMode?.phases[0]?.id
          },
          history: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: {
            totalTurns: 0,
            totalPlayTime: 0
          }
        }
        
        // セッション数制限チェック
        const sessions = get().sessions
        if (sessions.length >= MAX_SESSIONS) {
          // 最も古いセッションを削除
          const sortedSessions = [...sessions].sort((a, b) => 
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
          )
          set({ sessions: [...sortedSessions.slice(1), newSession] })
        } else {
          set({ sessions: [...sessions, newSession] })
        }
        
        set({ activeSession: newSession })
      },
      
      loadSession: (sessionId: string) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (session) {
          set({ activeSession: session })
        }
      },
      
      deleteSession: (sessionId: string) => {
        set(state => ({
          sessions: state.sessions.filter(s => s.id !== sessionId),
          activeSession: state.activeSession?.id === sessionId ? null : state.activeSession
        }))
      },
      
      saveSession: (sessionId: string, name?: string) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) return
        
        const saveId = generateSaveId()
        const gameFile = session.gameFileId 
          ? get().gameFiles.find(f => f.id === session.gameFileId)
          : undefined
        
        const newSave: SavedGame = {
          id: saveId,
          session,
          gameFile,
          savedAt: new Date().toISOString(),
          name: name || `セーブ ${new Date().toLocaleString('ja-JP')}`,
          tags: []
        }
        
        // セーブ数制限チェック
        const savedGames = get().savedGames
        if (savedGames.length >= MAX_SAVED_GAMES) {
          // 最も古いセーブを削除
          const sortedSaves = [...savedGames].sort((a, b) => 
            new Date(a.savedAt).getTime() - new Date(b.savedAt).getTime()
          )
          set({ savedGames: [...sortedSaves.slice(1), newSave] })
        } else {
          set({ savedGames: [...savedGames, newSave] })
        }
      },
      
      // ゲーム実行
      sendAction: async (action: string, apiKey?: string, options?: { temperature?: number; responseLength?: string }): Promise<ActionResult> => {
        const session = get().activeSession
        if (!session) {
          return {
            success: false,
            message: 'アクティブなセッションがありません',
            stateChanges: {}
          }
        }
        
        if (!apiKey) {
          return {
            success: false,
            message: 'APIキーが設定されていません',
            stateChanges: {}
          }
        }
        
        set({ isLoading: true, error: null })
        
        try {
          const gameFile = session.gameFileId 
            ? get().gameFiles.find(f => f.id === session.gameFileId)
            : undefined
          
          console.log('Debug - sendAction:', {
            sessionId: session.id,
            gameFileId: session.gameFileId,
            foundGameFile: !!gameFile,
            gameFileName: gameFile?.name,
            gameFileMode: gameFile?.mode,
            hasStructuredMode: !!gameFile?.structuredMode,
            allGameFiles: get().gameFiles.map(f => ({ id: f.id, name: f.name }))
          })
          
          // 構造化モードでgameFileが見つからない場合はエラー
          if (session.mode === 'structured' && !gameFile) {
            console.error('Structured mode requires a game file but none was found')
            return {
              success: false,
              message: '構造化モードのゲームファイルが見つかりません。セッションを再作成してください。',
              stateChanges: {}
            }
          }
          
          // メッセージIDを生成
          const userMessageId = generateMessageId()
          const userMessage: GameMessage = {
            id: userMessageId,
            role: 'user',
            content: action,
            timestamp: new Date().toISOString(),
            metadata: {
              turn: session.gameState.turn
            }
          }
          
          // ユーザーメッセージを追加
          const updatedMessages = [...session.messages, userMessage]
          
          // メッセージ数制限チェック
          if (updatedMessages.length > MAX_MESSAGES_PER_SESSION) {
            // 古いメッセージを削除（最初の10件を削除）
            updatedMessages.splice(0, 10)
          }
          
          // 実際のAPI呼び出し
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
          const requestBody = {
            mode: session.mode,
            action: action,
            gameState: session.gameState,
            gameFile: gameFile,
            history: updatedMessages, // 全履歴を渡す
            options: {
              temperature: options?.temperature || 0.8,
              criticalMode: false,
              responseLength: options?.responseLength
            }
          }
          
          console.log('Sending request to API:', {
            ...requestBody,
            gameFileDetails: gameFile ? {
              id: gameFile.id,
              name: gameFile.name,
              mode: gameFile.mode,
              hasStructuredMode: !!gameFile.structuredMode,
              structuredModeKeys: gameFile.structuredMode ? Object.keys(gameFile.structuredMode) : [],
              parameters: gameFile.structuredMode?.parameters?.length || 0,
              phases: gameFile.structuredMode?.phases?.length || 0,
              actions: gameFile.structuredMode?.actions?.length || 0,
              systemPrompt: gameFile.structuredMode?.systemPrompt?.substring(0, 50)
            } : 'NO GAME FILE'
          })
          
          const response = await fetch(`${apiUrl}/api/v1/textgame/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': apiKey
            },
            body: JSON.stringify({
              ...requestBody,
              modelId: getCurrentGeneratorModel('textgame')
            })
          })
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'APIエラー' }))
            console.error('API Error:', errorData)
            throw new Error(errorData.message || errorData.error || `HTTPエラー: ${response.status}`)
          }
          
          const responseData = await response.json()
          console.log('API Response:', responseData)
          
          if (!responseData.success || !responseData.data) {
            console.error('Invalid response format:', responseData)
            throw new Error(responseData.message || responseData.error || 'レスポンスの形式が不正です')
          }
          
          const result = responseData.data as TextGameResponse
          console.log('Parsed result:', result)
          
          // resultのsuccessフラグを確認
          if (!result.success) {
            console.error('Game action failed - Full result:', result)
            console.error('Error message:', result.message)
            console.error('Error details:', result.error)
            return {
              success: false,
              message: result.message || result.error || 'ゲームアクションが失敗しました',
              stateChanges: {}
            }
          }
          
          // 新しいゲーム状態を構築
          const newGameState: GameState = result.gameState || {
            ...session.gameState,
            ...result.stateChanges,
            turn: session.gameState.turn + 1
          }
          
          const gameMessageId = generateMessageId()
          const gameMessage: GameMessage = {
            id: gameMessageId,
            role: 'game',
            content: result.message,
            timestamp: new Date().toISOString(),
            metadata: {
              turn: newGameState.turn,
              result: {
                success: result.success,
                message: result.message,
                stateChanges: result.stateChanges || {},
                effects: result.effects,
                triggeredEvents: result.triggeredEvents,
                gameEnd: result.gameEnd
              }
            }
          }
          
          // 履歴に追加
          const newHistory = [...session.history]
          if (result.stateChanges) {
            newHistory.push(newGameState)
            if (newHistory.length > MAX_HISTORY_STATES) {
              newHistory.shift()
            }
          }
          
          // セッション更新
          const updatedSession: GameSession = {
            ...session,
            messages: [...updatedMessages, gameMessage],
            gameState: newGameState,
            history: newHistory,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...session.metadata,
              totalTurns: newGameState.turn,
              lastCheckpoint: newGameState.turn
            }
          }
          
          // セッションを更新
          set(state => ({
            sessions: state.sessions.map(s => 
              s.id === session.id ? updatedSession : s
            ),
            activeSession: updatedSession,
            isLoading: false
          }))
          
          // ゲーム終了チェック
          if (result.gameEnd?.ended) {
            const endMessage: GameMessage = {
              id: generateMessageId(),
              role: 'system',
              content: result.gameEnd.win 
                ? '🎉 ゲームクリア！おめでとうございます！' 
                : '💀 ゲームオーバー',
              timestamp: new Date().toISOString()
            }
            
            set(state => ({
              sessions: state.sessions.map(s => 
                s.id === session.id 
                  ? { ...updatedSession, messages: [...updatedSession.messages, endMessage] }
                  : s
              ),
              activeSession: { ...updatedSession, messages: [...updatedSession.messages, endMessage] }
            }))
          }
          
          return {
            success: result.success,
            message: result.message,
            stateChanges: result.stateChanges || {},
            effects: result.effects,
            triggeredEvents: result.triggeredEvents,
            gameEnd: result.gameEnd
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '不明なエラー'
          set({ error: errorMessage, isLoading: false })
          return {
            success: false,
            message: errorMessage,
            stateChanges: {}
          }
        }
      },
      
      processStructuredAction: async (actionId: string, params?: any, apiKey?: string, options?: { temperature?: number; responseLength?: string }): Promise<ActionResult> => {
        // 構造化モードのアクション処理
        const session = get().activeSession
        if (!session || session.mode !== 'structured') {
          return {
            success: false,
            message: '構造化モードのセッションがアクティブではありません',
            stateChanges: {}
          }
        }
        
        // アクションIDにパラメータを結合
        let fullAction = actionId
        if (params) {
          fullAction = `${actionId} ${JSON.stringify(params)}`
        }
        
        return get().sendAction(fullAction, apiKey, options)
      },
      
      rollbackToTurn: (turn: number) => {
        const session = get().activeSession
        if (!session) return
        
        const historicalState = session.history.find(h => h.turn === turn)
        if (historicalState) {
          const updatedSession: GameSession = {
            ...session,
            gameState: historicalState,
            messages: session.messages.filter(m => 
              (m.metadata?.turn || 0) <= turn
            ),
            updatedAt: new Date().toISOString()
          }
          
          set(state => ({
            sessions: state.sessions.map(s => 
              s.id === session.id ? updatedSession : s
            ),
            activeSession: updatedSession
          }))
        }
      },
      
      resetGame: () => {
        const session = get().activeSession
        if (!session) return
        
        const gameFile = session.gameFileId 
          ? get().gameFiles.find(f => f.id === session.gameFileId)
          : undefined
        
        const resetSession: GameSession = {
          ...session,
          messages: [],
          gameState: {
            parameters: gameFile?.structuredMode?.parameters.reduce((acc, p) => ({
              ...acc,
              [p.name]: p.initialValue
            }), {}) || {},
            turn: 0,
            flags: {},
            phase: gameFile?.structuredMode?.phases[0]?.id
          },
          history: [],
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          sessions: state.sessions.map(s => 
            s.id === session.id ? resetSession : s
          ),
          activeSession: resetSession
        }))
      },
      
      // ゲームファイル管理
      createGameFile: (file: Partial<GameFile>) => {
        const fileId = generateGameFileId()
        const newFile: GameFile = {
          id: fileId,
          name: file.name || '新しいゲーム',
          description: file.description || '',
          version: file.version || '1.0.0',
          mode: file.mode || 'free',
          freeMode: file.freeMode,
          structuredMode: file.structuredMode,
          metadata: {
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ...file.metadata
          }
        }
        
        // ゲームファイル数制限チェック
        const gameFiles = get().gameFiles
        if (gameFiles.length >= MAX_GAME_FILES) {
          // 最も古いファイルを削除
          const sortedFiles = [...gameFiles].sort((a, b) => 
            new Date(a.metadata.updatedAt).getTime() - new Date(b.metadata.updatedAt).getTime()
          )
          set({ gameFiles: [...sortedFiles.slice(1), newFile] })
        } else {
          set({ gameFiles: [...gameFiles, newFile] })
        }
      },
      
      // saveGameFileはcreateGameFileのエイリアスで、作成したファイルを返す
      saveGameFile: (file: GameFile) => {
        const fileId = file.id || generateGameFileId()
        const newFile: GameFile = {
          ...file,
          id: fileId,
          metadata: {
            ...file.metadata,
            updatedAt: new Date().toISOString()
          }
        }
        
        console.log('Saving game file:', {
          id: fileId,
          name: newFile.name,
          mode: newFile.mode,
          hasStructuredMode: !!newFile.structuredMode,
          structuredKeys: newFile.structuredMode ? Object.keys(newFile.structuredMode) : [],
          parameters: newFile.structuredMode?.parameters?.length || 0,
          phases: newFile.structuredMode?.phases?.length || 0,
          actions: newFile.structuredMode?.actions?.length || 0
        })
        
        // 既存のファイルがある場合は更新
        const existingFile = get().gameFiles.find(f => f.id === fileId)
        if (existingFile) {
          set(state => ({
            gameFiles: state.gameFiles.map(f => 
              f.id === fileId ? newFile : f
            )
          }))
        } else {
          // ゲームファイル数制限チェック
          const gameFiles = get().gameFiles
          if (gameFiles.length >= MAX_GAME_FILES) {
            // 最も古いファイルを削除
            const sortedFiles = [...gameFiles].sort((a, b) => 
              new Date(a.metadata.updatedAt).getTime() - new Date(b.metadata.updatedAt).getTime()
            )
            set({ gameFiles: [...sortedFiles.slice(1), newFile] })
          } else {
            set({ gameFiles: [...gameFiles, newFile] })
          }
        }
        
        return newFile
      },
      
      loadGameFile: (fileId: string) => {
        return get().gameFiles.find(f => f.id === fileId) || null
      },
      
      updateGameFile: (fileId: string, updates: Partial<GameFile>) => {
        set(state => ({
          gameFiles: state.gameFiles.map(f => 
            f.id === fileId 
              ? { 
                  ...f, 
                  ...updates,
                  metadata: {
                    ...f.metadata,
                    ...updates.metadata,
                    updatedAt: new Date().toISOString()
                  }
                }
              : f
          )
        }))
      },
      
      deleteGameFile: (fileId: string) => {
        set(state => ({
          gameFiles: state.gameFiles.filter(f => f.id !== fileId)
        }))
      },
      
      importGameFile: (jsonString: string) => {
        try {
          const file = JSON.parse(jsonString) as GameFile
          get().createGameFile(file)
        } catch (error) {
          console.error('Failed to import game file:', error)
        }
      },
      
      exportGameFile: (fileId: string) => {
        const file = get().gameFiles.find(f => f.id === fileId)
        return file ? JSON.stringify(file, null, 2) : ''
      },
      
      // 状態管理
      updateGameState: (updates: Partial<GameState>) => {
        const session = get().activeSession
        if (!session) return
        
        const updatedSession: GameSession = {
          ...session,
          gameState: {
            ...session.gameState,
            ...updates
          },
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          sessions: state.sessions.map(s => 
            s.id === session.id ? updatedSession : s
          ),
          activeSession: updatedSession
        }))
      },
      
      addToHistory: (state: GameState) => {
        const session = get().activeSession
        if (!session) return
        
        let history = [...session.history, state]
        
        // 履歴数制限
        if (history.length > MAX_HISTORY_STATES) {
          history = history.slice(-MAX_HISTORY_STATES)
        }
        
        const updatedSession: GameSession = {
          ...session,
          history,
          updatedAt: new Date().toISOString()
        }
        
        set(s => ({
          sessions: s.sessions.map(sess => 
            sess.id === session.id ? updatedSession : sess
          ),
          activeSession: updatedSession
        }))
      },
      
      getStateAtTurn: (turn: number) => {
        const session = get().activeSession
        if (!session) return null
        
        return session.history.find(h => h.turn === turn) || null
      },
      
      // ユーティリティ
      validateAction: (action: string) => {
        // アクション検証ロジック
        return action.length > 0 && action.length < 500
      },
      
      checkWinConditions: () => {
        // 勝利条件チェック（構造化モードのみ）
        return false
      },
      
      checkLoseConditions: () => {
        // 敗北条件チェック（構造化モードのみ）
        return false
      },
      
      compressHistory: (keepRecent?: number) => {
        const session = get().activeSession
        if (!session) return
        
        const keep = keepRecent || 10
        const compressedHistory = session.history.slice(-keep)
        
        const updatedSession: GameSession = {
          ...session,
          history: compressedHistory,
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          sessions: state.sessions.map(s => 
            s.id === session.id ? updatedSession : s
          ),
          activeSession: updatedSession
        }))
      },
      
      // エクスポート/インポート
      exportSession: (sessionId: string, format: 'json' | 'text') => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) return ''
        
        if (format === 'text') {
          // テキスト形式でエクスポート
          let text = `ゲームタイトル: ${session.title}\n`
          text += `モード: ${session.mode}\n`
          text += `ターン: ${session.gameState.turn}\n\n`
          text += '=== 会話履歴 ===\n\n'
          
          session.messages.forEach(msg => {
            if (msg.role === 'user') {
              text += `> ${msg.content}\n\n`
            } else {
              text += `${msg.content}\n\n`
            }
          })
          
          return text
        } else {
          // JSON形式でエクスポート
          return JSON.stringify(session, null, 2)
        }
      },
      
      importSession: (data: string) => {
        try {
          const session = JSON.parse(data) as GameSession
          // IDを再生成して重複を防ぐ
          session.id = generateSessionId()
          
          set(state => ({
            sessions: [...state.sessions, session],
            activeSession: session
          }))
        } catch (error) {
          console.error('Failed to import session:', error)
        }
      },
      
      exportAll: () => {
        const data = {
          version: '1.0',
          exportDate: new Date().toISOString(),
          sessions: get().sessions,
          gameFiles: get().gameFiles,
          savedGames: get().savedGames
        }
        return JSON.stringify(data, null, 2)
      },
      
      importAll: (data: string) => {
        try {
          const parsed = JSON.parse(data)
          
          // IDを再生成して重複を防ぐ
          if (parsed.sessions) {
            parsed.sessions.forEach((s: GameSession) => {
              s.id = generateSessionId()
            })
          }
          if (parsed.gameFiles) {
            parsed.gameFiles.forEach((f: GameFile) => {
              f.id = generateGameFileId()
            })
          }
          if (parsed.savedGames) {
            parsed.savedGames.forEach((s: SavedGame) => {
              s.id = generateSaveId()
            })
          }
          
          set({
            sessions: parsed.sessions || [],
            gameFiles: parsed.gameFiles || [],
            savedGames: parsed.savedGames || []
          })
        } catch (error) {
          console.error('Failed to import data:', error)
        }
      },
      
      // メッセージ管理
      deleteMessagesAfter: (sessionId: string, messageId: string) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) return
        
        const messageIndex = session.messages.findIndex(m => m.id === messageId)
        if (messageIndex === -1) return
        
        // 指定されたメッセージより後のメッセージを削除
        const updatedMessages = session.messages.slice(0, messageIndex + 1)
        
        set(state => ({
          sessions: state.sessions.map(s => 
            s.id === sessionId 
              ? { ...s, messages: updatedMessages }
              : s
          ),
          activeSession: state.activeSession?.id === sessionId
            ? { ...state.activeSession, messages: updatedMessages }
            : state.activeSession
        }))
      },
      
      retryUserMessage: async (sessionId: string, messageId: string, apiKey: string, options?: { temperature?: number; responseLength?: string }): Promise<ActionResult> => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) {
          return {
            success: false,
            message: 'セッションが見つかりません',
            stateChanges: {}
          }
        }
        
        const messageIndex = session.messages.findIndex(m => m.id === messageId)
        if (messageIndex === -1) {
          return {
            success: false,
            message: 'メッセージが見つかりません',
            stateChanges: {}
          }
        }
        
        const targetMessage = session.messages[messageIndex]
        if (targetMessage.role !== 'user') {
          return {
            success: false,
            message: 'ユーザーメッセージのみ再送信できます',
            stateChanges: {}
          }
        }
        
        // 指定されたメッセージより後のメッセージを削除
        get().deleteMessagesAfter(sessionId, messageId)
        
        // 現在のセッションをアクティブにする
        get().loadSession(sessionId)
        
        // メッセージを再送信
        return get().sendAction(targetMessage.content, apiKey, options)
      },
      
      // メモリ管理
      cleanupOldSessions: () => {
        const sessions = get().sessions
        if (sessions.length <= MAX_SESSIONS / 2) return
        
        // 最新のセッションを残す
        const sortedSessions = [...sessions].sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
        
        set({ sessions: sortedSessions.slice(0, MAX_SESSIONS / 2) })
      },
      
      cleanupOldSaves: () => {
        const saves = get().savedGames
        if (saves.length <= MAX_SAVED_GAMES / 2) return
        
        // 最新のセーブを残す
        const sortedSaves = [...saves].sort((a, b) => 
          new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
        )
        
        set({ savedGames: sortedSaves.slice(0, MAX_SAVED_GAMES / 2) })
      },
      
      getMemoryUsage: () => {
        const sessions = get().sessions
        const totalMessages = sessions.reduce((acc, s) => acc + s.messages.length, 0)
        
        return {
          sessions: sessions.length,
          saves: get().savedGames.length,
          gameFiles: get().gameFiles.length,
          messages: totalMessages
        }
      }
    }),
    {
      name: 'textgame-storage',
      version: 1,
      storage: createIndexedDBStorage('textgame-storage'),
      onRehydrateStorage: () => (state) => {
        // hydration完了時にフラグを立てる
        if (state) {
          state.isHydrated = true
          // 古いデータのクリーンアップ
          state.cleanupOldSessions()
          state.cleanupOldSaves()
        }
      },
      partialize: (state) => ({
        // DataCloneError防止のため、データのみを保存
        sessions: state.sessions,
        activeSession: state.activeSession,
        gameFiles: state.gameFiles,
        savedGames: state.savedGames,
        currentMode: state.currentMode
        // 注意: 以下は永続化しない
        // - isLoading（実行時の状態）
        // - error（実行時のエラー）
        // - isHydrated（実行時のフラグ）
      })
    }
  )
)
